<template>
  <ThePopUp
    :is-open="dataCenter.isShowPopUp"
    :mask-closable="false"
    :max-width="'450px'"
    :min-height="'auto'"
    @close="handleClose"
  >
    <div
      class="py-2 tablet:px-2"
      :class="(isSubmitting || isLoading) && 'pointer-events-none opacity-60'"
    >
      <div class="relative mb-5">
        <strong class="block pr-6 text-2xl">
          {{ t('title') }}
        </strong>
        <button class="absolute right-0 top-1 h-6 w-6" @click="handleClose">
          <IconClose class="h-full w-full text-black" />
        </button>
      </div>

      <div class="mb-5">
        <p class="mb-1">{{ t('description') }}</p>

        <FieldSelect
          id="form_data-center-code"
          v-model="selectDataCenterCode"
          name=""
          :required="false"
          :is-error="isError"
          :error-message="t('error-required-message')"
        >
          <option value="" disabled>{{ t('select') }}</option>
          <option
            v-for="item in dataCenter.dataCenters"
            :key="item.code"
            :value="item.code"
          >
            {{ item.name }}
          </option>
        </FieldSelect>
      </div>

      <div class="flex justify-end">
        <TheButton
          :disabled="isSubmitting"
          :title="t('button-name')"
          class="flex w-[120px] items-center justify-center"
          @click="handleClick"
        >
          <span class="text-sm leading-6">{{ t('button-name') }}</span>
          <IconLoader v-if="isSubmitting" class="ml-1 h-4 w-4" />
        </TheButton>
      </div>
    </div>
  </ThePopUp>
</template>

<script setup lang="ts">
  const { t } = useI18n({ useScope: 'local' });

  const pricingStore = usePricingStore();
  const { dataCenter } = storeToRefs(pricingStore);

  const selectDataCenterCode = ref<string>('');
  const isLoading = ref<boolean>(false);
  const isSubmitting = ref<boolean>(false);
  const isError = ref<boolean>(false);

  const handleClose = (): void => {
    selectDataCenterCode.value = '';
    isError.value = false;
    pricingStore.RESET_DATA_CENTER();
  };

  const handleClick = async (): Promise<void> => {
    if (!selectDataCenterCode.value) {
      isError.value = true;
      return;
    }
    if (isLoading.value || isSubmitting.value) {
      return;
    }
    isSubmitting.value = true;
    await pricingStore.POST_SUBSCRIBE_SERVICE(selectDataCenterCode.value);
    isSubmitting.value = false;
  };

  watch(selectDataCenterCode, (value) => {
    if (value) {
      isError.value = false;
    } else {
      isError.value = false;
    }
  });

  watch(
    () => dataCenter.value.isShowPopUp,
    async (value) => {
      if (value) {
        isLoading.value = true;
        await pricingStore.GET_DATA_CENTERS();
        isLoading.value = false;
      } else {
        handleClose();
      }
    },
    { immediate: true }
  );

  onBeforeRouteLeave(() => {
    handleClose();
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "Deploy This App to EnSaaS",
    "description": "Select Data Center:",
    "button-name": "Get Service",
    "select": "Select",
    "error-required-message": "The DataCenterCode field is required."
  },
  "en-jp": {
    "title": "Deploy This App to EnSaaS",
    "description": "Select Data Center:",
    "button-name": "Get Service",
    "select": "Select",
    "error-required-message": "The DataCenterCode field is required."
  },
  "en-tw": {
    "title": "Deploy This App to EnSaaS",
    "description": "Select Data Center:",
    "button-name": "Get Service",
    "select": "Select",
    "error-required-message": "The DataCenterCode field is required."
  },
  "en-kr": {
    "title": "Deploy This App to EnSaaS",
    "description": "Select Data Center:",
    "button-name": "Get Service",
    "select": "Select",
    "error-required-message": "The DataCenterCode field is required."
  },
  "en-americas": {
    "title": "Deploy This App to EnSaaS",
    "description": "Select Data Center:",
    "button-name": "Get Service",
    "select": "Select",
    "error-required-message": "The DataCenterCode field is required."
  },
  "en-asia-pacific": {
    "title": "Deploy This App to EnSaaS",
    "description": "Select Data Center:",
    "button-name": "Get Service",
    "select": "Select",
    "error-required-message": "The DataCenterCode field is required."
  },
  "en-emea": {
    "title": "Deploy This App to EnSaaS",
    "description": "Select Data Center:",
    "button-name": "Get Service",
    "select": "Select",
    "error-required-message": "The DataCenterCode field is required."
  },
  "ja-jp": {
    "title": "このアプリを EnSaaS にデプロイ",
    "description": "データセンターを選択：",
    "button-name": "サービスを取得",
    "select": "選択",
    "error-required-message": "DataCenter は必須項目です。"
  },
  "zh-cn": {
    "title": "将此应用部署到 EnSaaS",
    "description": "选择数据中心：",
    "button-name": "获取服务",
    "select": "选择",
    "error-required-message": "DataCenter 栏位为必填。"
  },
  "zh-tw": {
    "title": "將此應用程式部署至 EnSaaS",
    "description": "選擇資料中心：",
    "button-name": "取得服務",
    "select": "選擇",
    "error-required-message": "DataCenter 欄位為必填。"
  }
}
</i18n>
